import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getCookie } from '../Utils/cookieUtils';

// Enable withCredentials globally for all axios requests
axios.defaults.withCredentials = true;

// Add a request interceptor to automatically add the CSRF token
axios.interceptors.request.use(
  (config) => {
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to capture 5xx errors in Sentry
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 403 CSRF errors and retry once
    if (error.response && error.response.status === 403 && !originalRequest._retry) {
      const message = error.response.data?.message || '';
      if (message.toLowerCase().includes('csrf')) {
        originalRequest._retry = true;
        try {
          // Fetch a fresh CSRF token
          const refreshResult = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/system/csrf-token`
          );

          if (refreshResult.data && refreshResult.data.csrfToken) {
            // Update the header for the retry, using body fallback if cookie isn't ready
            const newToken = getCookie('XSRF-TOKEN') || refreshResult.data.csrfToken;
            originalRequest.headers['x-csrf-token'] = newToken;

            return axios(originalRequest);
          }
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      }
    }

    if (error.response && error.response.status >= 500) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response.status,
          data: error.response.data,
        },
      });
    }
    return Promise.reject(error);
  }
);

export default axios;
